import React from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

// Timeline Components
const TimelineContainer = styled.div`
  position: relative;
  padding-left: 32px;
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 32px;

  &:last-child {
    padding-bottom: 0;
  }
`;

const TimelineLine = styled.div`
  position: absolute;
  left: -24px;
  top: 8px;
  bottom: -8px;
  width: 2px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};

  ${TimelineItem}:last-child & {
    display: none;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: -28px;
  top: 4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => {
    switch (props.type) {
      case 'success':
        return props.theme?.colors?.success || '#22C55E';
      case 'error':
        return '#EF4444';
      case 'info':
        return '#3B82F6';
      default:
        return props.theme?.colors?.primary || '#fa4616';
    }
  }};
  border: 2px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  box-shadow: 0 0 0 4px
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#f3f4f6'};
`;

const TimelineContent = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};
  border-radius: 8px;
  padding: 16px;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const TimelineTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.primary
      : props.theme.colors.blueBlack};
`;

const TimelineTime = styled.div`
  font-size: 12px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.muted};
  white-space: nowrap;
`;

const TimelineDescription = styled.div`
  font-size: 13px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.textGray.secondary
      : props.theme.colors.textGray.secondary};
  line-height: 1.5;
`;

const TimelineDocs = () => {
  const timelineData = [
    {
      id: 1,
      title: 'Campaign Sent',
      description: 'Welcome email sent to 1,250 customers',
      time: '2 hours ago',
      type: 'success',
    },
    {
      id: 2,
      title: 'Export Completed',
      description: 'Customer report exported successfully (CSV format)',
      time: '5 hours ago',
      type: 'info',
    },
    {
      id: 3,
      title: 'Import Started',
      description: 'Importing 500 new customers from CSV',
      time: '1 day ago',
      type: 'default',
    },
    {
      id: 4,
      title: 'Segment Created',
      description: 'Created "Super Fans" segment with 342 customers',
      time: '2 days ago',
      type: 'default',
    },
  ];

  return (
    <div>
      <PageHeader>
        <Title>Timeline Pattern</Title>
        <Subtitle>
          Vertical timeline component for displaying chronological events, activities,
          and history. Perfect for activity feeds, audit logs, and event sequences.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Live Example</SectionTitle>
        <SectionDescription>
          A basic timeline showing different event types with timestamps.
        </SectionDescription>

        <ComponentDemo label="Timeline Component" align="stretch">
          <div style={{ maxWidth: '600px' }}>
            <TimelineContainer>
              {timelineData.map((item, index) => (
                <TimelineItem key={item.id}>
                  <TimelineLine />
                  <TimelineDot type={item.type} />
                  <TimelineContent>
                    <TimelineHeader>
                      <TimelineTitle>{item.title}</TimelineTitle>
                      <TimelineTime>{item.time}</TimelineTime>
                    </TimelineHeader>
                    <TimelineDescription>{item.description}</TimelineDescription>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </TimelineContainer>
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Timeline Variations</SectionTitle>
        <SectionDescription>
          Timeline items can have different colors to represent different event types.
        </SectionDescription>

        <ComponentDemo label="Different Event Types" align="stretch">
          <div style={{ maxWidth: '600px' }}>
            <TimelineContainer>
              <TimelineItem>
                <TimelineLine />
                <TimelineDot type="success" />
                <TimelineContent>
                  <TimelineHeader>
                    <TimelineTitle>Success Event</TimelineTitle>
                    <TimelineTime>Just now</TimelineTime>
                  </TimelineHeader>
                  <TimelineDescription>
                    Operation completed successfully
                  </TimelineDescription>
                </TimelineContent>
              </TimelineItem>

              <TimelineItem>
                <TimelineLine />
                <TimelineDot type="error" />
                <TimelineContent>
                  <TimelineHeader>
                    <TimelineTitle>Error Event</TimelineTitle>
                    <TimelineTime>5 min ago</TimelineTime>
                  </TimelineHeader>
                  <TimelineDescription>
                    Operation failed and needs attention
                  </TimelineDescription>
                </TimelineContent>
              </TimelineItem>

              <TimelineItem>
                <TimelineLine />
                <TimelineDot type="info" />
                <TimelineContent>
                  <TimelineHeader>
                    <TimelineTitle>Info Event</TimelineTitle>
                    <TimelineTime>10 min ago</TimelineTime>
                  </TimelineHeader>
                  <TimelineDescription>
                    Informational message for the user
                  </TimelineDescription>
                </TimelineContent>
              </TimelineItem>

              <TimelineItem>
                <TimelineDot type="default" />
                <TimelineContent>
                  <TimelineHeader>
                    <TimelineTitle>Default Event</TimelineTitle>
                    <TimelineTime>1 hour ago</TimelineTime>
                  </TimelineHeader>
                  <TimelineDescription>
                    Standard activity log entry
                  </TimelineDescription>
                </TimelineContent>
              </TimelineItem>
            </TimelineContainer>
          </div>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <CodeBlock>
{`import styled from 'styled-components';

const TimelineContainer = styled.div\`
  position: relative;
  padding-left: 32px;
\`;

const TimelineItem = styled.div\`
  position: relative;
  padding-bottom: 32px;

  &:last-child {
    padding-bottom: 0;
  }
\`;

const TimelineLine = styled.div\`
  position: absolute;
  left: -24px;
  top: 8px;
  bottom: -8px;
  width: 2px;
  background: \${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};

  \${TimelineItem}:last-child & {
    display: none;
  }
\`;

const TimelineDot = styled.div\`
  position: absolute;
  left: -28px;
  top: 4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: \${(props) => {
    switch (props.type) {
      case 'success':
        return props.theme?.colors?.success || '#22C55E';
      case 'error':
        return '#EF4444';
      case 'info':
        return '#3B82F6';
      default:
        return props.theme?.colors?.primary || '#fa4616';
    }
  }};
  border: 2px solid
    \${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  box-shadow: 0 0 0 4px
    \${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#f3f4f6'};
\`;

const TimelineContent = styled.div\`
  background: \${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    \${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'};
  border-radius: 8px;
  padding: 16px;
\`;

// Usage
<TimelineContainer>
  {events.map((event) => (
    <TimelineItem key={event.id}>
      <TimelineLine />
      <TimelineDot type={event.type} />
      <TimelineContent>
        <TimelineHeader>
          <TimelineTitle>{event.title}</TimelineTitle>
          <TimelineTime>{event.time}</TimelineTime>
        </TimelineHeader>
        <TimelineDescription>{event.description}</TimelineDescription>
      </TimelineContent>
    </TimelineItem>
  ))}
</TimelineContainer>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Complete Example</SectionTitle>
        <CodeBlock>
{`import React from 'react';
import styled from 'styled-components';

const Timeline = ({ events }) => {
  return (
    <TimelineContainer>
      {events.map((event, index) => (
        <TimelineItem key={event.id}>
          <TimelineLine />
          <TimelineDot type={event.type} />
          <TimelineContent>
            <TimelineHeader>
              <TimelineTitle>{event.title}</TimelineTitle>
              <TimelineTime>{event.time}</TimelineTime>
            </TimelineHeader>
            <TimelineDescription>{event.description}</TimelineDescription>
            {event.metadata && (
              <TimelineMetadata>
                {Object.entries(event.metadata).map(([key, value]) => (
                  <MetadataItem key={key}>
                    <MetadataLabel>{key}:</MetadataLabel>
                    <MetadataValue>{value}</MetadataValue>
                  </MetadataItem>
                ))}
              </TimelineMetadata>
            )}
          </TimelineContent>
        </TimelineItem>
      ))}
    </TimelineContainer>
  );
};

// Usage
const ActivityFeed = () => {
  const activities = [
    {
      id: 1,
      title: 'Campaign Sent',
      description: 'Welcome email sent to all new customers',
      time: '2 hours ago',
      type: 'success',
      metadata: {
        Recipients: '1,250',
        'Open Rate': '42%',
      },
    },
    {
      id: 2,
      title: 'Export Completed',
      description: 'Customer report exported successfully',
      time: '5 hours ago',
      type: 'info',
      metadata: {
        Format: 'CSV',
        Records: '500',
      },
    },
  ];

  return <Timeline events={activities} />;
};`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Enhanced Timeline with Icons</SectionTitle>
        <SectionDescription>
          You can enhance timeline items with icons to make events more recognizable.
        </SectionDescription>

        <CodeBlock>
{`const TimelineDotWithIcon = styled.div\`
  position: absolute;
  left: -32px;
  top: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: \${(props) => {
    switch (props.type) {
      case 'success':
        return props.theme?.colors?.success || '#22C55E';
      case 'error':
        return '#EF4444';
      case 'info':
        return '#3B82F6';
      default:
        return props.theme?.colors?.primary || '#fa4616';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  svg {
    width: 16px;
    height: 16px;
  }
\`;

// Usage with icon
<TimelineItem>
  <TimelineLine />
  <TimelineDotWithIcon type="success">
    <svg fill="currentColor" viewBox="0 0 16 16">
      <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
    </svg>
  </TimelineDotWithIcon>
  <TimelineContent>...</TimelineContent>
</TimelineItem>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <Card title="✅ Do" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Show events in reverse chronological order (newest first)
              <br />
              • Use color coding to distinguish event types
              <br />
              • Include relative timestamps (e.g., "2 hours ago")
              <br />
              • Keep event descriptions concise and scannable
              <br />
              • Group related events together
              <br />• Add metadata for important details
            </div>
          </Card>

          <Card title="❌ Don't" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Don't show too many events at once (use pagination/infinite scroll)
              <br />
              • Don't use too many different colors (stick to 3-4 types)
              <br />
              • Don't make timestamps absolute unless necessary
              <br />
              • Don't hide important information in collapsed states
              <br />• Don't forget to handle empty states
            </div>
          </Card>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Common Use Cases</SectionTitle>
        <Card>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            <strong>Activity Feeds:</strong>
            <br />• User actions and interactions
            <br />• System notifications and alerts
            <br />• Campaign activity history
            <br />
            <br />
            <strong>Audit Logs:</strong>
            <br />• User login/logout events
            <br />• Data changes and modifications
            <br />• Permission and role changes
            <br />
            <br />
            <strong>Customer History:</strong>
            <br />• Purchase history and orders
            <br />• Support tickets and interactions
            <br />• Communication history (emails, SMS)
            <br />
            <br />
            <strong>Order Tracking:</strong>
            <br />• Order status updates
            <br />• Shipping and delivery milestones
            <br />• Payment and refund events
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Accessibility Considerations</SectionTitle>
        <Card>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • Use semantic HTML (ordered list with list items)
            <br />
            • Provide descriptive labels for screen readers
            <br />
            • Ensure sufficient color contrast for dots and text
            <br />
            • Make timestamps readable and understandable
            <br />
            • Support keyboard navigation for interactive elements
            <br />• Include ARIA labels for timeline structure
          </div>
        </Card>
      </Section>
    </div>
  );
};

export default TimelineDocs;